import { platform } from '@/shared/lib/nativePlatform';
import { env } from '@/shared/lib/env';
import { JSON_API_CONTENT_TYPE, STORAGE_KEYS } from '@/shared/lib/constants';
import { parseErrorsFromResponse } from './jsonapi/errors';
import { isNetworkFailure, NetworkError } from './errorMessage';

import { leggiChiave } from '@/shared/lib/storage';
import { decodeToken } from '@/shared/lib/jwt';

type AuthHeadersProvider = () => {
  token: string | null;
  fingerprint: string | null;
  /** tenantId del JWT (informativo, NON inviato come header). */
  tenantId: string | null;
  lang: string;
};

let authProvider: AuthHeadersProvider = () => ({
  token: null,
  fingerprint: leggiChiave(STORAGE_KEYS.fingerprint),
  tenantId: leggiChiave(STORAGE_KEYS.tenantId),
  lang: leggiChiave(STORAGE_KEYS.lang) ?? env.VITE_DEFAULT_LANG,
});

/** Allows the AuthProvider to inject live token/tenant context into the HTTP client. */
export function setAuthHeadersProvider(provider: AuthHeadersProvider): void {
  authProvider = provider;
}

/**
 * Handler iniettato dall'AuthProvider per il refresh-on-401 (BE v1.8.2: diversi
 * endpoint che prima erano 500 ora rispondono 401). Ritorna il nuovo access token,
 * oppure null se il refresh fallisce (in quel caso l'handler ha già fatto logout).
 */
type RefreshHandler = () => Promise<string | null>;
let refreshHandler: RefreshHandler | null = null;
let refreshInFlight: Promise<string | null> | null = null;

export function setAuthRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

/**
 * Single-flight: 401 simultanei condividono lo stesso refresh, non N refresh paralleli.
 *
 * Esportato perché anche il rinnovo dell'`AuthProvider` (all'avvio, e al timer prima della
 * scadenza) passi di qui: finché ne faceva uno per conto suo, all'avvio ne partivano due.
 */
export function refreshSessionOnce(): Promise<string | null> {
  if (!refreshHandler) return Promise.resolve(null);
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Sotto questo margine un token si rinnova prima di usarlo, non dopo il 401. */
const EXPIRY_MARGIN_MS = 10_000;

/**
 * Il token da mandare, rinnovato **prima** di partire quando si sa già che non passerebbe.
 *
 * All'avvio l'access token non c'è: si conserva solo il refresh token, e il primo rinnovo lo
 * lancia l'`AuthProvider`. Ma le prime letture (`/account/me` di `TermsGate` per prima) partono
 * dagli effetti dei figli, che girano prima di quelli del provider: andavano **senza
 * `Authorization`**, prendevano 401 e rinnovavano da sole. Sul dev, nella settimana al 28-09-2026,
 * erano 442 `/me` su 992 — tutti senza header, nessuno con un token scaduto. Lo stesso vale al
 * risveglio di un portatile, quando il timer del rinnovo non è scattato in tempo.
 *
 * Si rinnova solo se c'è un refresh token salvato: senza, la richiesta parte com'è e il server
 * decide, come prima. Un token che non si decodifica, o che non dichiara `exp`, si usa così
 * com'è: non sapere quando scade non è un motivo per rinnovarlo a ogni chiamata.
 */
async function bearerFor(anonymous: boolean): Promise<{ token: string | null; refreshed: boolean }> {
  const { token } = authProvider();
  if (anonymous) return { token: null, refreshed: false };
  if (token && !expiresSoon(token)) return { token, refreshed: false };
  if (!refreshHandler || !leggiChiave(STORAGE_KEYS.refreshToken)) return { token, refreshed: false };
  const fresh = await refreshSessionOnce();
  return { token: fresh ?? token, refreshed: true };
}

function expiresSoon(token: string): boolean {
  const exp = decodeToken(token)?.exp;
  if (!exp) return false;
  return exp * 1000 - Date.now() <= EXPIRY_MARGIN_MS;
}

/**
 * Piattaforma reale del runtime: `web` in browser, `ios`/`android` dentro l'app.
 *
 * ⚠️ Interrogava il guscio precedente, che nel pacchetto non c'e': rispondeva **`web` dentro
 * l'app**. La
 * conseguenza non era un guasto visibile, ed e' per questo che e' durata — l'app si annunciava al
 * server come un browser. Due effetti:
 *
 * · `platform: 'web'` invece di `'app'`, quindi il BE risolveva la riga `Setup` **sbagliata**
 *   (`environment == platform`): le impostazioni del web applicate alle sessioni dell'app;
 * · `appPlatform: 'web'`, quindi nell'audit del server non risulta nessuna richiesta da Android.
 *
 * ⚠️ **Questa correzione cambia cio' che il server vede**, e va provata con un accesso vero
 * dall'APK: la riga `Setup` di `app` puo' avere impostazioni diverse da quella di `web`, e il
 * commento su `getPlatformEnv()` qui sotto racconta che con `platform: 'android'` il login andava
 * in `NullReferenceException` su `setup.useMD5`. Il valore `'app'` e' quello che il BE si aspetta
 * (`AccountController.cs:264`), ma la prova sta su un telefono, non qui.
 */
function getDevicePlatform(): 'web' | 'ios' | 'android' {
  const p = platform();
  return p === 'ios' || p === 'android' ? p : 'web';
}

/**
 * Valore dell'header `platform`, che per il BE è l'**ambiente** e non il dispositivo.
 *
 * Il server lo usa per risolvere la riga `Setup` (`environment == platform`), e di righe ne
 * esistono **due**: `web` e `app`. Mandare `android` faceva cercare un `Setup` inesistente e
 * `AccountController.Login` andava in `NullReferenceException` sulla riga successiva
 * (`setup.useMD5`) — un 500 al login, ma solo dall'app: dal browser il valore `web` esisteva
 * e non se ne accorgeva nessuno.
 *
 * Il dispositivo vero viaggia a parte, in `appPlatform`: è il BE stesso a leggerlo così
 * quando `platform == 'app'` (`AccountController.cs:264`).
 */
function getPlatformEnv(): 'web' | 'app' {
  return getDevicePlatform() === 'web' ? 'web' : 'app';
}

export interface HttpRequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip JSON serialization (e.g. for FormData uploads). */
  json?: boolean;
  /** Skip Bearer header (e.g. login/refresh). */
  anonymous?: boolean;
  /** Number of retry attempts on 5xx/429. */
  retries?: number;
  /**
   * Endpoint flavor:
   * - 'jsonapi' (default): uses `application/vnd.api+json` for both Accept and Content-Type
   * - 'plain': uses `application/json` (auth endpoints, custom controllers that don't speak JSON:API)
   */
  endpointType?: 'jsonapi' | 'plain';
}

const MAX_RETRIES = 3;
const BACKOFF_BASE_MS = 200;

function isRawBody(b: unknown): b is BodyInit {
  return (
    typeof b === 'string' ||
    (typeof FormData !== 'undefined' && b instanceof FormData) ||
    (typeof Blob !== 'undefined' && b instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && (b instanceof ArrayBuffer || ArrayBuffer.isView(b))) ||
    (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) ||
    (typeof ReadableStream !== 'undefined' && b instanceof ReadableStream)
  );
}

export interface HttpStatusResult<T = unknown> {
  /** Status HTTP grezzo della risposta (204 → body null). */
  status: number;
  /** Body parsato (JSON o testo), oppure null se assente/non parsabile. */
  body: T | null;
}

/**
 * Il 401 è una **decisione applicativa**, non una sessione scaduta.
 *
 * Sul BE lo stesso status copre due cose diverse, e il discriminatore è il **corpo**
 * (`BE-RESPONSE-TO-FE-CLOCK-IN-PAYLOAD-v1`, addendum del 12 ago 2026):
 *
 * | | corpo |
 * |---|---|
 * | sessione scaduta / token assente | **nessun JSON**: testo del middleware (`Status Code: 401`) |
 * | rifiuto applicativo (es. firma NFC non valida) | JSON con un `code`, come qualunque altro errore |
 *
 * Trattarli allo stesso modo costa tre cose, tutte visibili all'utente: un refresh inutile, la
 * richiesta **ritentata** (su una timbratura è un secondo tentativo di scrittura), e — se il
 * refresh fallisce proprio in quell'istante — un **logout** causato da un tag clonato.
 *
 * Si richiede un `code` esplicito, non un JSON qualunque: dove il server non lo dichiara restiamo
 * sul comportamento vecchio, perché quella è la forma di chi *non* ha deciso niente sul merito.
 */
function is401Application(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as { code?: unknown; errors?: unknown };
  // Forma plain: `{ error, code }`. Forma JSON:API: `errors[0].code`.
  if (typeof b.code === 'string' && b.code.length > 0) return true;
  if (!Array.isArray(b.errors)) return false;
  return b.errors.some((e) => typeof (e as { code?: unknown })?.code === 'string'
    && ((e as { code: string }).code.length > 0));
}

/**
 * Variante di httpRequest per i workflow custom del BE che comunicano l'esito
 * SOLO via status HTTP, senza documento JSON:API in risposta — es. il workflow
 * POST /api/user-profiles (creazione/associazione utente) che ritorna
 * 200/201/406/409/412/424/428/451 + 400/422 a body vuoto. A differenza di
 * httpRequest NON lancia sugli status >= 400: ritorna { status, body } e lascia
 * la decisione al chiamante. Mantiene Authorization, refresh-on-401
 * (single-flight) e retry su 5xx/429.
 */
export async function httpRequestStatus<T = unknown>(path: string, opts: HttpRequestOptions = {}): Promise<HttpStatusResult<T>> {
  const { method = 'GET', body, anonymous = false, retries = MAX_RETRIES, headers, endpointType = 'jsonapi', ...rest } = opts;
  // Quando il body e' raw (FormData/Blob/ecc), skippiamo automaticamente JSON serialization
  // e lasciamo che il browser/runtime imposti il Content-Type giusto (multipart con boundary).
  const useJson = opts.json !== false && !isRawBody(body);
  const auth = authProvider();

  const contentType = endpointType === 'plain' ? 'application/json' : JSON_API_CONTENT_TYPE;
  const baseHeaders: Record<string, string> = {
    Accept: useJson ? contentType : '*/*',
    'platform': getPlatformEnv(),
    'appPlatform': getDevicePlatform(),
    'userLang': auth.lang,
  };
  if (useJson && body !== undefined) {
    baseHeaders['Content-Type'] = contentType;
  }
  if (auth.fingerprint) baseHeaders['fingerPrint'] = auth.fingerprint;
  const bearer = await bearerFor(anonymous);
  if (bearer.token) baseHeaders['Authorization'] = `Bearer ${bearer.token}`;
  // NOTA: il cross-tenant override (BE v1.8.3+) si applica via filter JSON:API
  // su tenant.id (list) o data.relationships.tenant (POST), NON via header HTTP.
  // Eccezione: i workflow custom (es. user-profiles) usano gli header TenantId/
  // TenantDestinationId esplicitamente passati dal chiamante in opts.headers.

  const url = path.startsWith('http') ? path : `${env.VITE_API_BASE_URL}${path}`;

  const serializedBody: BodyInit | undefined =
    body === undefined ? undefined : isRawBody(body) ? body : JSON.stringify(body);

  let attempt = 0;
  /* Se il token l'abbiamo appena rinnovato e il server risponde 401 lo stesso, un secondo rinnovo
     non cambierebbe la risposta. */
  let did401Retry = bearer.refreshed;
  let lastErr: unknown;
  while (attempt <= retries) {
    try {
      const response = await fetch(url, {
        ...rest,
        method,
        headers: { ...baseHeaders, ...(headers as Record<string, string> | undefined) },
        body: serializedBody,
      });

      if (response.status === 204) return { status: 204, body: null };

      const respContentType = response.headers.get('content-type') ?? '';
      const isJsonResp = respContentType.includes('json');
      const parsed = isJsonResp ? await response.json().catch(() => null) : await response.text().catch(() => null);

      // 401 → un solo tentativo di refresh + retry. Skip per le richieste anonymous
      // (login/refresh/change-tenant), così l'endpoint di refresh non innesca un loop, e skip
      // quando il 401 porta un `code`: quello è un rifiuto nel merito, non un token da rinnovare
      // (vedi `e401Applicativo`).
      if (response.status === 401 && !anonymous && !did401Retry && refreshHandler
        && !is401Application(parsed)) {
        const newToken = await refreshSessionOnce();
        if (newToken) {
          did401Retry = true;
          baseHeaders['Authorization'] = `Bearer ${newToken}`;
          continue; // ritenta con il token fresco, senza consumare i retry 5xx
        }
        // refresh fallito: l'handler ha già fatto logout/redirect → propaga lo status 401
      }
      if ((response.status >= 500 || response.status === 429) && attempt < retries) {
        const retryAfter = Number(response.headers.get('Retry-After')) || BACKOFF_BASE_MS * 2 ** attempt;
        await sleep(retryAfter);
        attempt++;
        continue;
      }
      return { status: response.status, body: parsed as T | null };
    } catch (err) {
      // Network-level failure: retry with backoff
      lastErr = err;
      if (attempt >= retries) break;
      await sleep(BACKOFF_BASE_MS * 2 ** attempt);
      attempt++;
    }
  }
  /* Il `TypeError` del browser («Failed to fetch», «Load failed», …) non si propaga: il suo
     testo finirebbe in un alert, in inglese e senza dire se riprovare abbia senso. Vedi
     `errorMessage.ts`. */
  if (lastErr === undefined || isNetworkFailure(lastErr)) throw new NetworkError(lastErr);
  throw lastErr;
}

export async function httpRequest<T = unknown>(path: string, opts: HttpRequestOptions = {}): Promise<T> {
  const { status, body } = await httpRequestStatus<T>(path, opts);
  if (status === 204) return undefined as T;
  if (status >= 400) throw parseErrorsFromResponse(status, body);
  return body as T;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Helper tipato per multipart upload. Sostituisce il pattern
 *   httpRequest(url, { method: 'POST', body: form as unknown as BodyInit, json: false, headers: {} })
 * con
 *   httpFormRequest<Resp>(url, form)
 * Lascia che il runtime decida boundary/Content-Type del multipart.
 */
export function httpFormRequest<T = unknown>(
  path: string,
  formData: FormData,
  opts: Omit<HttpRequestOptions, 'body' | 'json' | 'method'> & { method?: 'POST' | 'PUT' | 'PATCH' } = {},
): Promise<T> {
  return httpRequest<T>(path, { ...opts, method: opts.method ?? 'POST', body: formData });
}

/**
 * Scarica una risorsa BINARIA (es. export XLSX server-side) come Blob, riusando
 * Authorization + refresh-on-401 dell'auth provider. NON usare httpRequest per i
 * binari: quello fa solo .json()/.text() e corromperebbe il file. Se il server
 * risponde con un errore JSON (es. 422 TIMESHEET_EXPORT_FORMAT_UNSUPPORTED), lo
 * propaga come JsonApiHttpError così getUserMessage() mostra il messaggio noto.
 */
export async function httpBlobRequest(path: string, opts: HttpRequestOptions = {}): Promise<Blob> {
  return (await httpBlobResult(path, opts)).blob;
}

/** Il file **con** gli header della risposta. Vedi `httpBlobResult`. */
export interface BlobResult {
  blob: Blob;
  headers: Headers;
  /** Nome proposto dal server (`Content-Disposition`), `null` se non dichiarato. */
  fileName: string | null;
}

/**
 * Come `httpBlobRequest`, ma restituisce anche gli **header**.
 *
 * Serve quando il server dichiara fuori dal corpo qualcosa che il chiamante deve dire
 * all'utente. Caso concreto: `GET /api/logs/export` ha un tetto di 10.000 righe e lo annuncia
 * con `X-Log-Export-Rows` / `X-Log-Export-Truncated`, non con un `meta` nel body — in
 * streaming il corpo è già partito quando si scopre di aver raggiunto il tetto. Senza leggere
 * quegli header, un export troncato è indistinguibile da uno completo, ed è il tipo di file su
 * cui poi si prendono decisioni.
 */
export async function httpBlobResult(path: string, opts: HttpRequestOptions = {}): Promise<BlobResult> {
  const { method = 'GET', body, anonymous = false, headers, ...rest } = opts;
  const auth = authProvider();
  const baseHeaders: Record<string, string> = {
    Accept: '*/*',
    'platform': getPlatformEnv(),
    'appPlatform': getDevicePlatform(),
    'userLang': auth.lang,
  };
  if (auth.fingerprint) baseHeaders['fingerPrint'] = auth.fingerprint;
  const bearer = await bearerFor(anonymous);
  if (bearer.token) baseHeaders['Authorization'] = `Bearer ${bearer.token}`;
  const url = path.startsWith('http') ? path : `${env.VITE_API_BASE_URL}${path}`;
  const serializedBody: BodyInit | undefined =
    body === undefined ? undefined : isRawBody(body) ? body : JSON.stringify(body);

  const doFetch = () => fetch(url, {
    ...rest, method,
    headers: { ...baseHeaders, ...(headers as Record<string, string> | undefined) },
    body: serializedBody,
  });

  /* ⚠️ Le due metà di questo `if` vengono da due lavori diversi e **servono tutte e due**: il
     nostro involucro che trasforma un errore di rete in una risposta leggibile, e la guardia di
     main che impedisce di rinnovare il token due volte di fila. Tenerne una sola avrebbe tolto
     silenziosamente l'altra. */
  let response = await fetchOrNetworkError(doFetch);
  if (response.status === 401 && !anonymous && !bearer.refreshed && refreshHandler) {
    /* Il corpo si legge su un **clone**: se il 401 non è una sessione scaduta non si rinnova
       niente, e l'originale resta leggibile qui sotto per costruire l'errore. */
    const ct401 = response.headers.get('content-type') ?? '';
    const parsed401 = ct401.includes('json') ? await response.clone().json().catch(() => null) : null;
    if (!is401Application(parsed401)) {
      const newToken = await refreshSessionOnce();
      if (newToken) {
        baseHeaders['Authorization'] = `Bearer ${newToken}`;
        response = await fetchOrNetworkError(doFetch);
      }
    }
  }
  if (response.status >= 400) {
    const ct = response.headers.get('content-type') ?? '';
    const parsed = ct.includes('json') ? await response.json().catch(() => null) : null;
    throw parseErrorsFromResponse(response.status, parsed);
  }
  return {
    blob: await response.blob(),
    headers: response.headers,
    fileName: fileNameFromDisposition(response.headers.get('content-disposition')),
  };
}


/** Come nel resto del client: una richiesta senza risposta esce come `NetworkError`, non come
 *  il `TypeError` del browser. */
async function fetchOrNetworkError(run: () => Promise<Response>): Promise<Response> {
  try {
    return await run();
  } catch (err) {
    throw isNetworkFailure(err) ? new NetworkError(err) : err;
  }
}

/** `attachment; filename="logs-20260811-1630.csv"` → `logs-20260811-1630.csv`. */
function fileNameFromDisposition(value: string | null): string | null {
  if (!value) return null;
  // `filename*=UTF-8''…` ha la precedenza quando c'è: è la forma che porta gli accenti.
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(value);
  if (star?.[1]) { try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* forma malformata: si prova quella semplice */ } }
  const plain = /filename="?([^";]+)"?/i.exec(value);
  return plain?.[1]?.trim() ?? null;
}
